import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  StyleSheet,
  ScrollView,
  View,
  RefreshControl,
  Pressable,
  Platform,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import { HistoryText as Text, HistoryMenu, HistoryViewToggle, useHistoryColors } from '@/components/Tools/ToolHistoryUI';
import { getToolGridColumnCount, getToolGridCardWidthPx } from '@/components/Tools/toolGridLayout';
import { getHistoryToolName } from '@/components/Tools/toolHistoryPresentation';
import Icon from '@/components/Icon';
import StatusContainer from '@/components/StatusContainer';
import ToolHistoryCard from '@/components/Tools/ToolHistoryCard';
import ToolHistoryRow from '@/components/Tools/ToolHistoryRow';
import { useToolStore, useToastStore, useAuthStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';

const EMPTY_RUNS = [];

const SECTION_META = {
  today:     { titleKey: 'pages.tools.historySectionToday',     fallback: 'Today',         icon: 'sun' },
  yesterday: { titleKey: 'pages.tools.historySectionYesterday', fallback: 'Yesterday',     icon: 'moon' },
  thisWeek:  { titleKey: 'pages.tools.historySectionThisWeek',  fallback: 'This week',     icon: 'calendar-week' },
  thisMonth: { titleKey: 'pages.tools.historySectionThisMonth', fallback: 'This month',    icon: 'calendar' },
  earlier:   { titleKey: 'pages.tools.historySectionEarlier',   fallback: 'Earlier',       icon: 'clock-rotate-left' },
};
const SECTION_ORDER = ['today', 'yesterday', 'thisWeek', 'thisMonth', 'earlier'];

const STATUS_SCOPES = [
  { id: 'all',       labelKey: 'pages.tools.statusFilter_all',       fallback: 'All',         icon: 'list',               colorKey: 'primary' },
  { id: 'completed', labelKey: 'pages.tools.statusFilter_completed', fallback: 'Completed',   icon: 'circle-check',       colorKey: 'success' },
  { id: 'failed',    labelKey: 'pages.tools.statusFilter_failed',    fallback: 'Failed',      icon: 'circle-exclamation', colorKey: 'danger' },
  { id: 'running',   labelKey: 'pages.tools.statusFilter_running',   fallback: 'In flight',   icon: 'spinner',            colorKey: 'warning' },
];

function runMatchesStatus(run, statusFilter) {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'running') return run.status === 'running' || run.status === 'pending';
  return run.status === statusFilter;
}

function runMatchesTool(run, toolFilter) {
  if (toolFilter === 'all') return true;
  const slug = run?.tool?.slug || run?.toolSlug;
  return slug === toolFilter;
}

// Bucket runs into roughly human-friendly groups so the grid breathes a bit
// more than a single endless wall of cards. Returns ordered groups so the
// most-recent activity is always on top.
function groupRunsByDate(runs) {
  const now    = new Date();
  const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yest   = today - 86400000;
  const weekAgo = today - 7 * 86400000;
  const monthAgo = today - 30 * 86400000;

  const buckets = { today: [], yesterday: [], thisWeek: [], thisMonth: [], earlier: [] };
  for (const run of runs) {
    const ts = run?.createdAt ? new Date(run.createdAt).getTime() : 0;
    if (ts >= today)        buckets.today.push(run);
    else if (ts >= yest)    buckets.yesterday.push(run);
    else if (ts >= weekAgo) buckets.thisWeek.push(run);
    else if (ts >= monthAgo) buckets.thisMonth.push(run);
    else                    buckets.earlier.push(run);
  }
  return buckets;
}

// Build a single lowercase haystack per run for cheap substring searching.
function buildSearchHaystack(run) {
  const parts = [];
  if (run?.tool?.name) parts.push(run.tool.name);
  if (run?.tool?.slug) parts.push(run.tool.slug);
  if (run?.toolSlug) parts.push(run.toolSlug);
  if (run?.compiledPrompt) parts.push(run.compiledPrompt);
  if (run?.resultText) parts.push(run.resultText);
  if (Array.isArray(run?.resultSegments)) {
    for (const seg of run.resultSegments) {
      if (seg?.content) parts.push(seg.content);
    }
  }
  if (run?.inputs && typeof run.inputs === 'object') {
    try { parts.push(JSON.stringify(run.inputs)); } catch (_) { /* noop */ }
  }
  if (Array.isArray(run?.resultFiles)) {
    for (const f of run.resultFiles) {
      if (f?.name) parts.push(f.name);
    }
  }
  return parts.join(' ').toLowerCase();
}

// ─── Styles ───────────────────────────────────────────────────────────────

const createStyles = (colors, isXS, insetBottom = 0) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.solid_color_primary },
  scroll: { flex: 1, width: '100%' },
  scrollContent: { paddingBottom: insetBottom + 60 },
  contentWrap: { width: '100%', maxWidth: 1160, alignSelf: 'center', paddingHorizontal: isXS ? 16 : 32, paddingTop: isXS ? 20 : 32 },
  heading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 24 },
  title: { fontSize: isXS ? 28 : 32, lineHeight: 38, letterSpacing: -0.8 },
  search: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 48, paddingLeft: 16, paddingRight: 4,
    borderRadius: 12, backgroundColor: colors.solid_color_secondary, borderWidth: 1, borderColor: 'transparent' },
  searchInput: { flex: 1, minWidth: 0, fontSize: 15, paddingVertical: 12, color: colors.text_color_primary,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) },
  toolbar: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 12, marginBottom: 24 },
  filters: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', flex: 1, minWidth: 200 },
  options: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  inner: { width: '100%' },
  section: { marginBottom: 32 },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, width: '100%' },
  list: { width: '100%', gap: 8 },
  loadingBlock: { paddingVertical: 80, alignItems: 'center', gap: 12 },
  emptyHero: { paddingVertical: 80, alignItems: 'center', gap: 14 },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_secondary },
  ctaBtn: { minHeight: 44, paddingHorizontal: 18, justifyContent: 'center', alignItems: 'center', borderRadius: 10, backgroundColor: colors.text_color_primary, marginTop: 8 },
  clearBtn: { paddingHorizontal: 12, minHeight: 44, justifyContent: 'center', borderRadius: 8 },
});

export default function ToolHistoryPage() {
  const colors = useHistoryColors();
  const { t } = useTranslation();
  const { isXS, windowWidth } = useResponsiveness();
  const [bodyWidth, setBodyWidth] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const gridWidth = bodyWidth || Math.min(1160, windowWidth) - (isXS ? 32 : 64);
  const cardWidth = getToolGridCardWidthPx(gridWidth, getToolGridColumnCount(gridWidth));
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toolFilter,   setToolFilter]   = useState('all');
  const [sortOrder,    setSortOrder]    = useState('newest');
  const [viewMode,     setViewMode]     = useState('grid');

  const allRuns        = useToolStore(s => s.runsByToolSlug.__all__) || EMPTY_RUNS;
  const fetchRuns      = useToolStore(s => s.fetchRuns);
  const isLoading      = useToolStore(s => s.isLoadingRunsByToolSlug?.__all__);
  const error          = useToolStore(s => s.runsErrorByToolSlug?.__all__);
  const deleteRun      = useToolStore(s => s.deleteRun);
  const addToast       = useToastStore(s => s.actions.addToast);
  const userId         = useAuthStore(s => s.user?.id);
  const hasUser        = Boolean(userId);
  const [deletingRunId, setDeletingRunId] = useState(null);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  useEffect(() => {
    // `fetchRuns({})` returns the user's full run history — skip for
    // anonymous visitors (would 401) and render the sign-in CTA panel
    // below instead.
    if (!hasUser) return;
    fetchRuns({}).catch(() => {});
  }, [fetchRuns, hasUser]);

  const onRefresh = useCallback(async () => {
    if (!hasUser) return;
    setRefreshing(true);
    try {
      await fetchRuns({});
    } catch (_) {
      // The store exposes the refresh error for the retry UI.
    } finally {
      setRefreshing(false);
    }
  }, [fetchRuns, hasUser]);

  const toolScopeRows = useMemo(() => {
    const seen = new Map();
    for (const r of allRuns) {
      const slug = r?.tool?.slug || r?.toolSlug;
      if (!slug || seen.has(slug)) continue;
      seen.set(slug, {
        id: slug,
        label: getHistoryToolName(r),
        icon: r?.tool?.icon || 'wand-magic-sparkles',
      });
    }
    return Array.from(seen.values());
  }, [allRuns]);

  const haystacks = useMemo(() => {
    const map = new Map();
    for (const r of allRuns) {
      const id = r?.id || r?._id;
      if (!id) continue;
      map.set(id, buildSearchHaystack(r));
    }
    return map;
  }, [allRuns]);

  const filteredRuns = useMemo(() => {
    let list = allRuns;

    if (statusFilter !== 'all') {
      list = list.filter(r => runMatchesStatus(r, statusFilter));
    }
    if (toolFilter !== 'all') {
      list = list.filter(r => runMatchesTool(r, toolFilter));
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        const id = r?.id || r?._id;
        const hay = id ? haystacks.get(id) : buildSearchHaystack(r);
        return hay && hay.includes(q);
      });
    }

    if (sortOrder === 'oldest') {
      list = [...list].sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return ta - tb;
      });
    } else if (sortOrder === 'newest') {
      list = [...list].sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
    }

    return list;
  }, [allRuns, statusFilter, toolFilter, searchQuery, sortOrder, haystacks]);

  const groups = useMemo(() => groupRunsByDate(filteredRuns), [filteredRuns]);

  const handleOpenRun = useCallback((run) => {
    const slug = run?.tool?.slug || run?.toolSlug;
    if (!slug || !run?.id) return;
    router.push(`/tools/${slug}?runId=${encodeURIComponent(run.id)}`);
  }, [router]);

  const performDeleteRun = useCallback(async (run) => {
    if (!run?.id) return;
    setDeletingRunId(run.id);
    try {
      await deleteRun(run.id);
    } catch (e) {
      addToast?.(e?.message || 'Failed to delete run', 'error');
    } finally {
      setDeletingRunId(null);
    }
  }, [deleteRun, addToast]);

  const handleDeleteRun = useCallback((run) => {
    if (!run?.id) return;
    const toolPhrase = (run?.tool?.name || run?.toolSlug || '').trim()
      || (t('pages.tools.deleteRunUnknownTool') || 'this tool');
    const title = t('pages.tools.deleteRunTitle') || 'Delete this run?';
    const message = (t('pages.tools.deleteRunMessage') || 'This removes the run from your history for %TOOL%. You cannot undo this.')
      .replace('%TOOL%', toolPhrase);
    showConfirmationModal({
      title,
      message,
      confirmText: t('common.delete') || 'Delete',
      cancelText: t('common.cancel') || 'Cancel',
      isDangerous: true,
      options: {
        height: 180,
        width: 350,
      },
      onConfirm: () => {
        void performDeleteRun(run);
      },
    });
  }, [t, showConfirmationModal, performDeleteRun]);

  const clearAllFilters = useCallback(() => {
    setStatusFilter('all');
    setToolFilter('all');
    setSearchQuery('');
  }, []);

  const isFiltering = statusFilter !== 'all' || toolFilter !== 'all' || !!searchQuery.trim();
  const hasAnyRuns = allRuns.length > 0;
  const hasNoMatches = hasAnyRuns && filteredRuns.length === 0;

  const styles = useMemo(() => createStyles(colors, isXS, insets.bottom), [colors, isXS, insets.bottom]);
  const toolOptions = [{ id: 'all', label: t('pages.tools.historyAllTools') }, ...toolScopeRows];
  const statusOptions = STATUS_SCOPES.map(scope => ({ id: scope.id, label: t(scope.labelKey), icon: scope.icon }));
  const sortOptions = [
    { id: 'newest', label: t('pages.tools.historySortNewest') },
    { id: 'oldest', label: t('pages.tools.historySortOldest') },
  ];
  const sectionOrder = sortOrder === 'oldest' ? [...SECTION_ORDER].reverse() : SECTION_ORDER;

  const renderHistoryMain = () => (
    <View style={styles.inner}>
      {error && !isLoading && !hasAnyRuns ? (
        <StatusContainer
          type="error"
          message={error}
          actionText={t('pages.common.retry') || 'Retry'}
          onAction={onRefresh}
        />
      ) : isLoading && !hasAnyRuns ? (
        <View style={styles.loadingBlock}>
          <ActivityIndicator size="large" color={colors.text_color_secondary} />
          <Text size="s" color="secondary">
            {t('pages.tools.historyLoading') || 'Loading your runs…'}
          </Text>
        </View>
      ) : !hasAnyRuns ? (
        <View style={styles.emptyHero}>
          <View style={styles.emptyIcon}>
            <Icon name="clock-rotate-left" size={26} color={colors.text_color_secondary} />
          </View>
          <Text size="m" weight="bold" color="primary">
            {t('pages.tools.historyEmptyTitle') || 'Nothing here yet'}
          </Text>
          <Text size="s" color="secondary" style={{ textAlign: 'center', maxWidth: 380 }}>
            {t('pages.tools.historyEmptyCopy')
              || 'Your results will appear here after you use a tool.'}
          </Text>
          <Pressable
            style={styles.ctaBtn}
            onPress={() => router.push('/tools')}
            accessibilityRole="button"
          >
            <Text size="s" weight="bold" style={{ color: colors.solid_color_primary }}>
              {t('pages.tools.browseTools') || 'Browse tools'}
            </Text>
          </Pressable>
        </View>
      ) : hasNoMatches ? (
        <StatusContainer
          type="empty"
          message={searchQuery.trim()
            ? ((t('pages.tools.historyNoSearchMatches') || 'No runs match \u201c%QUERY%\u201d.').replace('%QUERY%', searchQuery.trim()))
            : (t('pages.tools.historyNoMatches') || 'No runs match these filters.')}
          actionText={t('pages.tools.historyClearFilters') || 'Clear filters'}
          onAction={clearAllFilters}
        />
      ) : (
        sectionOrder.filter((key) => groups[key].length > 0).map((key) => {
          const meta = SECTION_META[key];
          const list = groups[key];
          return (
            <View key={key} style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text tag="h2" size="m" weight="semibold" color="primary">
                  {t(meta.titleKey) || meta.fallback}
                </Text>
                <View>
                  <Text size="xxs" weight="bold" color="secondary">
                    {list.length}
                  </Text>
                </View>
              </View>
              {viewMode === 'list' ? (
                <View style={styles.list}>
                  {list.map((run) => (
                    <ToolHistoryRow
                      key={run.id}
                      run={run}
                      isDeleting={deletingRunId === run.id}
                      onOpen={handleOpenRun}
                      onDelete={handleDeleteRun}
                    />
                  ))}
                </View>
              ) : (
                <View style={styles.grid}>
                  {list.map((run) => (
                    <View key={run.id} style={{ width: cardWidth }}>
                      <ToolHistoryCard
                        run={run}
                        isDeleting={deletingRunId === run.id}
                        onOpen={handleOpenRun}
                        onDelete={handleDeleteRun}
                      />
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        })
      )}
    </View>
  );

  if (!hasUser) {
    return (
      <View style={styles.container}>
        <MetaHead title={(t('pages.tools.history') || 'Tool History')} noindex />
        <SignInCTAPanel
          icon="clock-rotate-left"
          source="tools_history"
          title="Sign in to see your tool history"
          subtitle="Every tool you run is saved here so you can revisit, remix, or re-run any output with one click."
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MetaHead title={(t('pages.tools.history') || 'Tool History')} noindex />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode={Platform.OS === 'ios' || Platform.OS === 'android' ? 'on-drag' : 'none'}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        }
      >
        <View style={styles.contentWrap}>
          <View style={styles.heading}>
            <View style={{ flex: 1, gap: 6 }}>
              <Text tag="h1" weight="bold" style={styles.title}>{t('pages.tools.historyTitle')}</Text>
              <Text size="s" color="secondary">{t('pages.tools.historyDescription')}</Text>
            </View>
            {!isXS && <Pressable accessibilityRole="button" onPress={() => router.push('/tools')} style={styles.clearBtn}>
              <Text size="s" weight="medium">{t('pages.tools.browseTools')}</Text>
            </Pressable>}
          </View>
          {hasAnyRuns && <>
            <View style={[styles.search, searchFocused && { borderColor: colors.text_color_tertiary }]}>
              <Icon name="magnifying-glass" size={16} color={colors.text_color_secondary} />
              <TextInput value={searchQuery} onChangeText={setSearchQuery} placeholder={t('pages.tools.historySearchPlaceholder')}
                accessibilityLabel={t('pages.tools.historySearchPlaceholder')} placeholderTextColor={colors.text_color_secondary}
                style={styles.searchInput} autoCorrect={false} autoCapitalize="none" returnKeyType="search"
                onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} />
              {!!searchQuery && <Pressable onPress={() => setSearchQuery('')} style={styles.clearBtn}
                accessibilityRole="button" accessibilityLabel={t('pages.tools.historyClearSearch')}>
                <Icon name="xmark" size={14} color={colors.text_color_secondary} />
              </Pressable>}
            </View>
            <View style={styles.toolbar}>
              <View style={styles.filters}>
                <HistoryMenu label={toolOptions.find(o => o.id === toolFilter)?.label || toolOptions[0].label}
                  accessibilityLabel={t('pages.tools.historyFilterByTool')} options={toolOptions} value={toolFilter} onChange={setToolFilter} />
                <HistoryMenu label={statusFilter === 'all' ? t('pages.tools.historyAllStatuses') : statusOptions.find(o => o.id === statusFilter)?.label}
                  accessibilityLabel={t('pages.tools.historyFilterByStatus')} options={statusOptions} value={statusFilter} onChange={setStatusFilter} />
                {isFiltering && <Pressable onPress={clearAllFilters} style={styles.clearBtn} accessibilityRole="button">
                  <Text size="xs" color="secondary">{t('pages.tools.historyClearFilters')}</Text>
                </Pressable>}
              </View>
              <View style={styles.options}>
                <HistoryMenu label={sortOptions.find(o => o.id === sortOrder).label} accessibilityLabel={t('pages.tools.historySort')}
                  options={sortOptions} value={sortOrder} onChange={setSortOrder} />
                <HistoryViewToggle value={viewMode} onChange={setViewMode}
                  listLabel={t('pages.tools.historyViewList')} gridLabel={t('pages.tools.historyViewGrid')} />
              </View>
            </View>
          </>}
          {error && hasAnyRuns && <StatusContainer type="error" message={error}
            actionText={t('pages.common.retry')} onAction={onRefresh} />}
          <View onLayout={event => setBodyWidth(event.nativeEvent.layout.width)}>
            {renderHistoryMain()}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
